import i18n from "../../../../utils/i18n";
import classes from "./industryActivity.module.scss";

export const ACTIVITY_TITLE = i18n.t("searchflow.activitytitle");
export const ACTIVITY_SUBTITLE = i18n.t("searchflow.activitysubtitle");

export const IndustryActivityLayout = ({ title, subtitle, children }) => (
  <div className={classes.root}>
    <div className={classes.scrollArea}>
      <div className={classes.content}>
        <div className={classes.mainColumn}>
          <header className={classes.header}>
            <h1 className={classes.title}>{title}</h1>
            {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
          </header>

          <div className={classes.rightColumn}>
            <div className={classes.cardsContainer}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ActivityRadio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const ActivityCheckbox = ({ checked }) => (
  <span
    className={`${classes.checkbox} ${checked ? classes.checkboxChecked : ""}`}
    aria-hidden="true"
  />
);

const ActivityOptionCard = ({
  label,
  selected,
  onClick,
  ariaLabel,
  selectionType = "checkbox",
}) => {
  const isRadio = selectionType === "radio";
  const Indicator = isRadio ? ActivityRadio : ActivityCheckbox;

  return (
    <button
      type="button"
      className={`${classes.card} ${selected ? classes.selectedRow : ""}`}
      onClick={onClick}
      role={isRadio ? "radio" : "checkbox"}
      aria-checked={selected}
      aria-label={ariaLabel || label}
    >
      <div className={classes.cardContent}>
        <div className={classes.cardTitle}>{label}</div>
      </div>
      <Indicator checked={selected} />
    </button>
  );
};

const ActivityOtherInput = ({
  value,
  onChange,
  onBlur,
  placeholder,
  id,
}) => (
  <div className={classes.otherField}>
    <input
      id={id}
      type="text"
      className={classes.otherInput}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
    />
  </div>
);

const toggleCheckboxHandler = (handler, group, name) => {
  handler({ target: { name, checked: !group[name] } });
};

export const VehicleActivityContent = ({
  vehicleIndustry,
  handleVehicleChange,
  handleVehicleOtherInputChange,
  handleVehicleOtherInputBlur,
}) => {
  const group = vehicleIndustry.differentialvat;
  const options = [
    {
      name: "automotivepartstrading",
      label: i18n.t("searchflow.automotivepartstrading"),
    },
    { name: "trading", label: i18n.t("searchflow.vehicletrading") },
    { name: "service", label: i18n.t("searchflow.vehicleservice") },
    { name: "other", label: i18n.t("searchflow.otheractivity") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={!!group[option.name]}
          selectionType="checkbox"
          onClick={() => toggleCheckboxHandler(handleVehicleChange, group, option.name)}
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="vehicle-other-input"
          value={group.otherinput || ""}
          onChange={handleVehicleOtherInputChange}
          onBlur={handleVehicleOtherInputBlur}
          placeholder={i18n.t("searchflow.vehicletypeplaceholder")}
        />
      ) : null}
    </>
  );
};

export const EtradeActivityContent = ({
  etradeIndustry,
  handleEtradeSellsChange,
  handleEtradeSellsOtherInputChange,
  handleEtradeSellsOtherInputBlur,
}) => {
  const group = etradeIndustry.etradesells;
  const options = [
    { name: "physicalproducts", label: i18n.t("searchflow.physicalproducts") },
    { name: "services", label: i18n.t("searchflow.etradeservices") },
    {
      name: "intellectualproperty",
      label: i18n.t("searchflow.intellectualproperty"),
    },
    { name: "other", label: i18n.t("searchflow.otheractivity") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={!!group[option.name]}
          selectionType="checkbox"
          onClick={() => toggleCheckboxHandler(handleEtradeSellsChange, group, option.name)}
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="etrade-other-input"
          value={group.otherinput || ""}
          onChange={handleEtradeSellsOtherInputChange}
          onBlur={handleEtradeSellsOtherInputBlur}
          placeholder={i18n.t("searchflow.etradeotherplaceholder")}
        />
      ) : null}
    </>
  );
};

export const ITActivityContent = ({
  ITIndustry,
  handleITChange,
  handleITOtherInputChange,
  handleITOtherInputBlur,
}) => {
  const group = ITIndustry.ittype;
  const options = [
    {
      name: "wagereviewprojectwork",
      label: i18n.t("searchflow.wagereviewprojectwork"),
      selected: Boolean(group.wagereview || group.projectwork),
    },
    {
      name: "ownprodsoftdev",
      label: i18n.t("searchflow.ownprodsoftdev"),
      selected: Boolean(group.ownprodsoftdev),
    },
    {
      name: "other",
      label: i18n.t("searchflow.otheractivity"),
      selected: Boolean(group.other),
    },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={option.selected}
          selectionType="checkbox"
          onClick={() =>
            handleITChange({
              target: {
                name: option.name,
                checked: !option.selected,
              },
            })
          }
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="it-other-input"
          value={group.otherinput || ""}
          onChange={handleITOtherInputChange}
          onBlur={handleITOtherInputBlur}
          placeholder={i18n.t("searchflow.itotherplaceholder")}
        />
      ) : null}
    </>
  );
};

export const AgricultureActivityContent = ({
  agricultureIndustry,
  handleAgricultureChange,
  handleAgricultureOtherInputChange,
  handleAgricultureOtherInputBlur,
}) => {
  const group = agricultureIndustry.agriculturetype;
  const options = [
    { name: "livestockfarming", label: i18n.t("searchflow.livestockfarming") },
    { name: "cropproduction", label: i18n.t("searchflow.cropproduction") },
    { name: "other", label: i18n.t("searchflow.otheractivity") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={!!group[option.name]}
          selectionType="checkbox"
          onClick={() => toggleCheckboxHandler(handleAgricultureChange, group, option.name)}
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="agriculture-other-input"
          value={group.otherinput || ""}
          onChange={handleAgricultureOtherInputChange}
          onBlur={handleAgricultureOtherInputBlur}
          placeholder={i18n.t("searchflow.agricultureotherplaceholder")}
        />
      ) : null}
    </>
  );
};

export const ArtActivityContent = ({
  artIndustry,
  handleArtChange,
  handleArtOtherInputChange,
  handleArtOtherInputBlur,
}) => {
  const group = artIndustry.arttype;
  const options = [
    { name: "createartworks", label: i18n.t("searchflow.createartworks") },
    { name: "performingartist", label: i18n.t("searchflow.performingartist") },
    { name: "tradeartworks", label: i18n.t("searchflow.tradeartworks") },
    { name: "other", label: i18n.t("searchflow.otheractivity") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={!!group[option.name]}
          selectionType="checkbox"
          onClick={() => toggleCheckboxHandler(handleArtChange, group, option.name)}
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="art-other-input"
          value={group.otherinput || ""}
          onChange={handleArtOtherInputChange}
          onBlur={handleArtOtherInputBlur}
          placeholder={i18n.t("searchflow.artotherplaceholder")}
        />
      ) : null}
    </>
  );
};

export const BeautyActivityContent = ({
  beautyIndustry,
  handleBeautyProductSalesChange,
}) => {
  const status = beautyIndustry.beautyproductsales.status;
  const options = [
    { value: "yes", label: i18n.t("general.yes") },
    { value: "no", label: i18n.t("general.no") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.value}
          label={option.label}
          selected={status === option.value}
          selectionType="radio"
          onClick={() =>
            handleBeautyProductSalesChange({
              target: { value: option.value },
            })
          }
        />
      ))}
    </>
  );
};

export const MedicineActivityContent = ({
  medicineIndustry,
  handleMedicineChange,
  handleMedicineOtherInputChange,
  handleMedicineOtherInputBlur,
}) => {
  const group = medicineIndustry.medicinetype;
  const options = [
    { name: "doctor", label: i18n.t("searchflow.doctor") },
    { name: "pharmacy", label: i18n.t("searchflow.pharmacy") },
    { name: "nurse", label: i18n.t("searchflow.nurse") },
    { name: "vet", label: i18n.t("searchflow.vet") },
    { name: "other", label: i18n.t("searchflow.otheractivity") },
  ];

  return (
    <>
      {options.map((option) => (
        <ActivityOptionCard
          key={option.name}
          label={option.label}
          selected={!!group[option.name]}
          selectionType="checkbox"
          onClick={() => toggleCheckboxHandler(handleMedicineChange, group, option.name)}
        />
      ))}
      {group.other ? (
        <ActivityOtherInput
          id="medicine-other-input"
          value={group.otherinput || ""}
          onChange={handleMedicineOtherInputChange}
          onBlur={handleMedicineOtherInputBlur}
          placeholder={i18n.t("searchflow.medicineotherplaceholder")}
        />
      ) : null}
    </>
  );
};
